/**
 * P2-2: Suspension Enforcement Context (FIXED - v2)
 * 
 * CRITICAL FIX: Single source of truth for provider suspension status
 * 
 * This context now properly integrates with ProviderOnboardingContext
 * to ensure consistent suspension state across:
 * - TreatX Admin lists (Approved/Suspended tabs)
 * - Provider modals
 * - Patient clinic search
 * - All enforcement checks
 */
import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useProviderOnboarding, ONBOARDING_STATUS } from './ProviderOnboardingContext';
import api from '../utils/api';

const SuspensionEnforcementContext = createContext();

export const useSuspensionEnforcement = () => {
  const context = useContext(SuspensionEnforcementContext);
  if (!context) {
    throw new Error('useSuspensionEnforcement must be used within SuspensionEnforcementProvider');
  }
  return context;
};

// Actions that are blocked when suspended
export const BLOCKED_ACTIONS = {
  RECEIVE_MARKETPLACE_LEAD: 'receive_marketplace_lead',
  SEND_OFFER: 'send_offer',
  ACCEPT_PAYMENT: 'accept_payment',
  ADVANCE_LEAD_STATUS: 'advance_lead_status',
  INVITE_TEAM_MEMBER: 'invite_team_member',
  UPGRADE_PACKAGE: 'upgrade_package',
  APPEAR_IN_SEARCH: 'appear_in_search'
};

// P0-INTEGRITY: Removed hardcoded CLINIC_TO_PROVIDER_NAME_MAP
// Clinic names are now resolved dynamically from the backend catalog API
const _clinicNameCache = {};

export const SuspensionEnforcementProvider = ({ children }) => {
  const { 
    providers, 
    pendingApplications,
    isProviderSuspended, 
    getSuspendedProviders,
    suspendProvider: contextSuspendProvider,
    reinstateProvider: contextReinstateProvider
  } = useProviderOnboarding();

  // P0-INTEGRITY: Resolve clinic name from API (cached)
  const resolveClinicName = useCallback(async (clinicId) => {
    if (!clinicId) return null;
    if (_clinicNameCache[clinicId]) return _clinicNameCache[clinicId];
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await api.get(`/public/catalog/clinics/${clinicId}`);
      {
        const data = res.data;
        if (data.found && data.clinic?.name) {
          _clinicNameCache[clinicId] = data.clinic.name;
          return data.clinic.name;
        }
      }
    } catch (e) { /* silent */ }
    return null;
  }, []);

  // Get all providers (both approved and pending) for lookup
  const allProviders = useMemo(() => {
    return [...providers, ...pendingApplications];
  }, [providers, pendingApplications]);

  // Find a provider by name (case-insensitive partial match)
  // Checks legalInfo.legalName, legalInfo.tradeName, and businessName
  const findProviderByName = useCallback((name) => {
    if (!name) return null;
    const nameLower = name.toLowerCase();
    return allProviders.find(p => {
      const legalName = p.legalInfo?.legalName?.toLowerCase() || '';
      const tradeName = p.legalInfo?.tradeName?.toLowerCase() || '';
      const businessName = p.businessName?.toLowerCase() || '';
      
      return legalName.includes(nameLower) ||
             tradeName.includes(nameLower) ||
             businessName.includes(nameLower) ||
             nameLower.includes(legalName) ||
             nameLower.includes(tradeName) ||
             nameLower.includes(businessName);
    });
  }, [allProviders]);

  // Check if a clinic is suspended by looking up the provider
  const isClinicSuspended = useCallback((clinicId) => {
    // P0-INTEGRITY: Use cached name (sync check — async resolution happens on mount)
    const clinicName = _clinicNameCache[clinicId];
    if (!clinicName) {
      // If no cached name, trigger async resolution and check provider by ID
      resolveClinicName(clinicId);
      return isProviderSuspended(clinicId);
    }

    // Find the provider by name
    const provider = findProviderByName(clinicName);
    if (!provider) {
      return false;
    }

    // Check if the provider is suspended
    return provider.status === ONBOARDING_STATUS.SUSPENDED;
  }, [findProviderByName, isProviderSuspended, resolveClinicName]);

  // Get suspension details for a clinic
  const getSuspensionDetails = useCallback((clinicId) => {
    const clinicName = _clinicNameCache[clinicId];
    const provider = findProviderByName(clinicName);
    
    if (!provider || provider.status !== ONBOARDING_STATUS.SUSPENDED) {
      return null;
    }

    return {
      isSuspended: true,
      suspendedAt: provider.suspendedAt,
      suspensionReason: provider.suspensionReason,
      suspendedBy: 'TreatX Admin',
      previousStatus: provider.previousStatus
    };
  }, [findProviderByName]);

  // Check if an action is allowed for a clinic
  const canPerformAction = useCallback((clinicId, action) => {
    const isSuspended = isClinicSuspended(clinicId);
    
    if (!isSuspended) {
      return { allowed: true };
    }

    const blockedActions = Object.values(BLOCKED_ACTIONS);
    
    if (blockedActions.includes(action)) {
      return {
        allowed: false,
        reason: 'Action disabled due to account suspension',
        suspensionDetails: getSuspensionDetails(clinicId)
      };
    }

    return { allowed: true };
  }, [isClinicSuspended, getSuspensionDetails]);

  // Filter clinics to exclude suspended ones (for patient search)
  // This is the CRITICAL function that ensures suspended providers don't appear
  const filterOutSuspendedClinics = useCallback((clinics) => {
    // Get all suspended provider names (from all name fields)
    const suspendedProviders = getSuspendedProviders();
    const suspendedNames = new Set();
    
    suspendedProviders.forEach(p => {
      if (p.legalInfo?.legalName) suspendedNames.add(p.legalInfo.legalName.toLowerCase());
      if (p.legalInfo?.tradeName) suspendedNames.add(p.legalInfo.tradeName.toLowerCase());
      if (p.businessName) suspendedNames.add(p.businessName.toLowerCase());
    });

    console.log('[Suspension Filter] Suspended provider names:', Array.from(suspendedNames));

    return clinics.filter(clinic => {
      const clinicNameLower = clinic.name?.toLowerCase() || '';
      
      // P0-INTEGRITY: Use cached clinic name from API (no hardcoded map)
      const cachedName = _clinicNameCache[clinic.id];
      if (cachedName) {
        const cachedNameLower = cachedName.toLowerCase();
        for (const suspendedName of suspendedNames) {
          if (cachedNameLower.includes(suspendedName) || suspendedName.includes(cachedNameLower)) {
            console.log(`[Suspension Filter] Excluding suspended clinic: ${clinic.name} (cached: ${cachedName})`);
            return false;
          }
        }
      }

      // Check by clinic name directly against suspended names
      for (const suspendedName of suspendedNames) {
        if (clinicNameLower.includes(suspendedName) || suspendedName.includes(clinicNameLower)) {
          console.log(`[Suspension Filter] Excluding clinic by name match: ${clinic.name}`);
          return false;
        }
      }

      return true;
    });
  }, [getSuspendedProviders]);

  // Suspend a clinic (delegates to ProviderOnboardingContext)
  const suspendClinic = useCallback((clinicId, reason) => {
    // P0-INTEGRITY: Use cached clinic name from API (no hardcoded map)
    const clinicName = _clinicNameCache[clinicId];
    const provider = findProviderByName(clinicName);
    
    if (provider) {
      contextSuspendProvider(provider.id, reason);
      return { success: true };
    }
    
    // If no provider found, try suspending by clinic ID directly
    contextSuspendProvider(clinicId, reason);
    return { success: true };
  }, [findProviderByName, contextSuspendProvider]);

  // Reinstate a clinic
  const reinstateClinic = useCallback((clinicId, newValidUntil) => {
    // P0-INTEGRITY: Use cached clinic name from API (no hardcoded map)
    const clinicName = _clinicNameCache[clinicId];
    const provider = findProviderByName(clinicName);
    
    if (provider) {
      contextReinstateProvider(provider.id, newValidUntil);
      return { success: true };
    }

    contextReinstateProvider(clinicId, newValidUntil);
    return { success: true };
  }, [findProviderByName, contextReinstateProvider]);

  // Get all suspended clinics (for admin view)
  const getAllSuspendedClinics = useCallback(() => {
    return getSuspendedProviders().map(p => ({
      clinicId: p.id,
      clinicName: p.businessName,
      ...p
    }));
  }, [getSuspendedProviders]);

  const value = {
    // Core checks - SINGLE SOURCE OF TRUTH
    isClinicSuspended,
    getSuspensionDetails,
    canPerformAction,
    filterOutSuspendedClinics,
    
    // Admin actions
    suspendClinic,
    reinstateClinic,
    getAllSuspendedClinics,
    
    // Constants
    BLOCKED_ACTIONS
  };

  return (
    <SuspensionEnforcementContext.Provider value={value}>
      {children}
    </SuspensionEnforcementContext.Provider>
  );
};

export default SuspensionEnforcementContext;
